import {
  formatMonthlyAmount,
  formatSubscriptionDate,
} from './businessSubscriptionsData';

export const TRANSACTION_TYPE_OPTIONS = [
  'Initial Subscription Charge',
  'Trial Conversion Charge',
  'Renewal Charge',
  'Upgrade Charge',
  'Recovery Charge',
  'Refund',
  'Dispute',
];

/** Display statuses used across charge / refund / dispute records */
export const TRANSACTION_STATUS_OPTIONS = [
  'Pending',
  'Succeeded',
  'Failed',
  'Cancelled',
  'Partially Refunded',
  'Refunded',
  'Disputed',
  'Action Required',
  'Open',
  'Won',
  'Lost',
  'Prevented',
];

export const TRANSACTION_DATE_RANGE_OPTIONS = [
  'All',
  'Today',
  'Last 7 Days',
  'Last 30 Days',
  'Custom Range',
];

/**
 * @typedef {Object} BusinessBillingTransaction
 * @property {string} id
 * @property {string} businessId
 * @property {string} businessName
 * @property {string} type
 * @property {'Basic'|'Pro'} plan
 * @property {number} amount
 * @property {string} currency
 * @property {string} status Trades Poster display status
 * @property {string} exactStripeStatus
 * @property {string|null} paymentBrand
 * @property {string|null} paymentLast4
 * @property {string} transactionDate ISO datetime
 * @property {string|null} billingPeriodStart ISO date
 * @property {string|null} billingPeriodEnd ISO date
 * @property {string|null} relatedTransactionId
 * @property {string|null} providerReference
 * @property {string|null} invoiceReference
 * @property {string|null} stripeReceiptUrl
 * @property {string|null} failureReason
 * @property {number|null} refundedAmount
 * @property {string|null} refundStatus
 * @property {string|null} disputeStatus
 * @property {string|null} disputeOutcome
 */

/** @type {BusinessBillingTransaction[]} */
const allBusinessBillingTransactions = [
  // Werribee Plumbing — sample set from specs
  {
    id: 'TXN-008732',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Renewal Charge',
    plan: 'Pro',
    amount: 19.99,
    currency: 'AUD',
    status: 'Succeeded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2026-08-07T06:00:00+10:00',
    billingPeriodStart: '2026-08-07',
    billingPeriodEnd: '2026-09-07',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1Gp99TYC',
    invoiceReference: 'in_1R9W32',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1Gp99TYC',
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-008401',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Renewal Charge',
    plan: 'Pro',
    amount: 99.0,
    currency: 'AUD',
    status: 'Partially Refunded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2026-07-07T06:00:00+10:00',
    billingPeriodStart: '2026-07-07',
    billingPeriodEnd: '2026-08-07',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1Gp88ABC',
    invoiceReference: 'in_1R8W01',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1Gp88ABC',
    failureReason: null,
    refundedAmount: 20.0,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-008402',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Refund',
    plan: 'Pro',
    amount: 20.0,
    currency: 'AUD',
    status: 'Succeeded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2026-07-10T11:30:00+10:00',
    billingPeriodStart: null,
    billingPeriodEnd: null,
    relatedTransactionId: 'TXN-008401',
    providerReference: 're_3Q8WsL2eZvKYlo2C1Gp20RF',
    invoiceReference: null,
    stripeReceiptUrl: null,
    failureReason: null,
    refundedAmount: 20.0,
    refundStatus: 'Succeeded',
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-007918',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Renewal Charge',
    plan: 'Pro',
    amount: 99.0,
    currency: 'AUD',
    status: 'Disputed',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2026-06-07T06:00:00+10:00',
    billingPeriodStart: '2026-06-07',
    billingPeriodEnd: '2026-07-07',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1Gp77DSP',
    invoiceReference: 'in_1R7W18',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1Gp77DSP',
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-007919',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Dispute',
    plan: 'Pro',
    amount: 99.0,
    currency: 'AUD',
    status: 'Won',
    exactStripeStatus: 'won',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2026-06-09T14:00:00+10:00',
    billingPeriodStart: null,
    billingPeriodEnd: null,
    relatedTransactionId: 'TXN-007918',
    providerReference: 'dp_1R7W19Dispute',
    invoiceReference: null,
    stripeReceiptUrl: null,
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: 'Won',
    disputeOutcome: 'Won',
  },
  {
    id: 'TXN-006210',
    businessId: 'BUS-9934',
    businessName: 'Werribee Plumbing Pty Ltd',
    type: 'Upgrade Charge',
    plan: 'Pro',
    amount: 50.0,
    currency: 'AUD',
    status: 'Succeeded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    transactionDate: '2024-03-20T09:15:00+11:00',
    billingPeriodStart: '2024-03-20',
    billingPeriodEnd: '2024-04-20',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpUpg',
    invoiceReference: 'in_1R6W10',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1GpUpg',
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },

  // Apex Drainage — Basic trial; no charge attempt yet (No Payment Yet)

  // Urban Tree — failed renewal + prior paid cycle (legacy captured amounts retained)
  {
    id: 'TXN-008850',
    businessId: 'BUS-3625',
    businessName: 'Urban Tree Services',
    type: 'Renewal Charge',
    plan: 'Basic',
    amount: 9.99,
    currency: 'AUD',
    status: 'Failed',
    exactStripeStatus: 'failed',
    paymentBrand: 'Mastercard',
    paymentLast4: '5521',
    transactionDate: '2026-08-26T06:00:00+10:00',
    billingPeriodStart: '2026-08-26',
    billingPeriodEnd: '2026-09-26',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpFail',
    invoiceReference: 'in_1R8U50',
    stripeReceiptUrl: null,
    failureReason: 'Card Declined',
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-008200',
    businessId: 'BUS-3625',
    businessName: 'Urban Tree Services',
    type: 'Renewal Charge',
    plan: 'Basic',
    amount: 9.99,
    currency: 'AUD',
    status: 'Succeeded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Mastercard',
    paymentLast4: '5521',
    transactionDate: '2026-07-26T06:00:00+10:00',
    billingPeriodStart: '2026-07-26',
    billingPeriodEnd: '2026-08-26',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpOk26',
    invoiceReference: 'in_1R8U26',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1GpOk26',
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },

  // Northside Electrical
  {
    id: 'TXN-008900',
    businessId: 'BUS-5810',
    businessName: 'Northside Electrical',
    type: 'Renewal Charge',
    plan: 'Pro',
    amount: 19.99,
    currency: 'AUD',
    status: 'Succeeded',
    exactStripeStatus: 'succeeded',
    paymentBrand: 'Visa',
    paymentLast4: '6610',
    transactionDate: '2026-08-01T06:00:00+10:00',
    billingPeriodStart: '2026-08-01',
    billingPeriodEnd: '2026-09-01',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpN900',
    invoiceReference: 'in_1R8N01',
    stripeReceiptUrl: 'https://dashboard.stripe.com/payments/pi_3Q8WsL2eZvKYlo2C1GpN900',
    failureReason: null,
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },

  // Rapid Roofing — inactive after failed recovery (legacy captured amounts retained)
  {
    id: 'TXN-007500',
    businessId: 'BUS-4478',
    businessName: 'Rapid Roofing Specialists',
    type: 'Recovery Charge',
    plan: 'Basic',
    amount: 9.99,
    currency: 'AUD',
    status: 'Failed',
    exactStripeStatus: 'failed',
    paymentBrand: 'Mastercard',
    paymentLast4: '4242',
    transactionDate: '2026-07-28T09:00:00+10:00',
    billingPeriodStart: null,
    billingPeriodEnd: null,
    relatedTransactionId: 'TXN-007480',
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpR750',
    invoiceReference: null,
    stripeReceiptUrl: null,
    failureReason: 'Insufficient Funds',
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
  {
    id: 'TXN-007480',
    businessId: 'BUS-4478',
    businessName: 'Rapid Roofing Specialists',
    type: 'Renewal Charge',
    plan: 'Basic',
    amount: 49.0,
    currency: 'AUD',
    status: 'Failed',
    exactStripeStatus: 'failed',
    paymentBrand: 'Mastercard',
    paymentLast4: '4242',
    transactionDate: '2026-07-21T06:00:00+10:00',
    billingPeriodStart: '2026-06-21',
    billingPeriodEnd: '2026-07-21',
    relatedTransactionId: null,
    providerReference: 'pi_3Q8WsL2eZvKYlo2C1GpR748',
    invoiceReference: 'in_1R7R480',
    stripeReceiptUrl: null,
    failureReason: 'Insufficient Funds',
    refundedAmount: null,
    refundStatus: null,
    disputeStatus: null,
    disputeOutcome: null,
  },
];

export function getTransactionsForBusiness(businessId) {
  if (!businessId) return [];
  const id = String(businessId).trim().toLowerCase();
  return allBusinessBillingTransactions
    .filter((txn) => String(txn.businessId).toLowerCase() === id)
    .slice()
    .sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));
}

export function formatTransactionPaymentMethod(txn) {
  if (!txn?.paymentBrand || !txn?.paymentLast4) return 'Not provided';
  return `${txn.paymentBrand} •••• ${txn.paymentLast4}`;
}

export function formatTransactionBillingPeriod(txn) {
  if (!txn?.billingPeriodStart || !txn?.billingPeriodEnd) return '—';
  return `${formatSubscriptionDate(txn.billingPeriodStart)}–${formatSubscriptionDate(txn.billingPeriodEnd)}`;
}

export function formatTransactionDateTime(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatTransactionDateOnly(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export { formatMonthlyAmount };

export function getTransactionDateRangeBounds(dateRange, customFrom, customTo) {
  const now = new Date();
  now.setHours(23, 59, 59, 999);

  if (!dateRange || dateRange === 'All') return { start: null, end: null };

  if (dateRange === 'Custom Range') {
    const start = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
    const end = customTo ? new Date(`${customTo}T23:59:59`) : null;
    return { start, end };
  }

  if (dateRange === 'Today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }

  if (dateRange === 'Last 7 Days') {
    const start = new Date(now);
    start.setDate(now.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }

  if (dateRange === 'Last 30 Days') {
    const start = new Date(now);
    start.setDate(now.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }

  return { start: null, end: null };
}

export function matchesTransactionSearch(txn, term) {
  if (!term) return true;
  const q = term.toLowerCase();
  return (
    (txn.id && txn.id.toLowerCase().includes(q)) ||
    (txn.providerReference && txn.providerReference.toLowerCase().includes(q)) ||
    (txn.invoiceReference && txn.invoiceReference.toLowerCase().includes(q)) ||
    (txn.relatedTransactionId && txn.relatedTransactionId.toLowerCase().includes(q))
  );
}

/** Conditional Outcome fields for the transaction popup */
export function getTransactionOutcomeFields(txn) {
  if (!txn) return [{ label: 'Outcome', value: 'No exception' }];

  const fields = [];

  if (txn.type === 'Refund' || txn.refundStatus || txn.refundedAmount != null) {
    if (txn.refundedAmount != null) {
      fields.push({ label: 'Refunded Amount', value: formatMonthlyAmount(txn.refundedAmount) });
    }
    if (txn.refundStatus) {
      fields.push({ label: 'Refund Status', value: txn.refundStatus });
    }
  }

  if (txn.type === 'Dispute' || txn.disputeStatus || txn.disputeOutcome) {
    if (txn.disputeStatus) {
      fields.push({ label: 'Dispute Status', value: txn.disputeStatus });
    }
    if (txn.disputeOutcome) {
      fields.push({ label: 'Dispute Outcome', value: txn.disputeOutcome });
    }
  }

  if (txn.status === 'Failed' || txn.failureReason) {
    fields.push({ label: 'Failure Reason', value: txn.failureReason || '—' });
  }

  if (txn.relatedTransactionId) {
    fields.push({ label: 'Related Transaction ID', value: txn.relatedTransactionId });
  }

  if (fields.length === 0) {
    return [{ label: 'Outcome', value: 'No exception' }];
  }

  return fields;
}
